/**
 * Utility for sorting assistants by favorites, default, general, and name
 */

interface SortableAssistant {
  id: string;
  name: string;
  specialty?: string;
}

interface SortAssistantsOptions {
  /** IDs of favorited assistants */
  favoriteIds?: string[];
  /** ID of the organization's default assistant */
  defaultAssistantId?: string;
  /** Whether to sort "General" assistants after favorites/default */
  sortGeneralFirst?: boolean;
  /** Whether to sort alphabetically by name at the end */
  sortAlphabetically?: boolean;
}

/**
 * Sorts assistants by:
 * 1. Favorites first
 * 2. Default assistant second (if provided)
 * 3. "General" assistants third (if sortGeneralFirst is true)
 * 4. Alphabetically by name (if sortAlphabetically is true)
 */
export function sortAssistants<T extends SortableAssistant>(
  assistants: T[],
  options: SortAssistantsOptions = {}
): T[] {
  const {
    favoriteIds = [],
    defaultAssistantId,
    sortGeneralFirst = false,
    sortAlphabetically = false,
  } = options;

  const hasFavorites = favoriteIds.length > 0;
  const hasDefault = !!defaultAssistantId;

  if (!hasFavorites && !hasDefault && !sortGeneralFirst && !sortAlphabetically) {
    return assistants;
  }

  return [...assistants].sort((a, b) => {
    // Favorites first
    const aIsFavorite = favoriteIds.includes(a.id);
    const bIsFavorite = favoriteIds.includes(b.id);
    if (aIsFavorite && !bIsFavorite) return -1;
    if (!aIsFavorite && bIsFavorite) return 1;

    // Default assistant second
    if (defaultAssistantId) {
      if (a.id === defaultAssistantId) return -1;
      if (b.id === defaultAssistantId) return 1;
    }

    // "General" assistants third
    if (sortGeneralFirst && a.specialty && b.specialty) {
      const aHasGeneral = a.specialty.toLowerCase().includes("general");
      const bHasGeneral = b.specialty.toLowerCase().includes("general");
      if (aHasGeneral && !bHasGeneral) return -1;
      if (!aHasGeneral && bHasGeneral) return 1;
    }

    // Alphabetically by name
    if (sortAlphabetically) {
      return a.name.localeCompare(b.name);
    }

    return 0;
  });
}

